<!-- eslint-disable rulesdir/no-retired-components -->
<template>
  <div
    class="komodo-checkbox"
    :class="{ checked, disabled, errored }"
  >
    <div
      v-if="$slots.title"
    >
      <slot name="title" />
    </div>
    <komodo-input-title
      v-else
      :title="title"
    />

    <label :class="['komodo-label', `komodo-${theme}`]">
      <input
        :id="id"
        ref="inputRef"
        v-model="model"
        type="checkbox"
        :value="value"
        :true-value="trueValue"
        :false-value="falseValue"
        :disabled="disabled"
        @change="onInputChange"
      >

      <div
        :class="['komodo-input', `komodo-${theme}`]"
        data-test="checkbox"
      >
        <komodo-icon
          icon="done"
          :class="['komodo-input-checkmark', `komodo-${theme}`]"
        />
      </div>

      <slot>
        <span>{{ label }}</span>
      </slot>
    </label>

    <komodo-errors :errors="errors" />
  </div>
</template>

<script lang="ts">
/* eslint-disable rulesdir/no-retired-components */
import { defineComponent, ref, Ref, watch, PropType, computed, nextTick, onMounted } from 'vue'

import KomodoInputTitle from 'shared/components/KomodoInputTitle.vue'
import KomodoErrors from 'shared/components/KomodoErrors.vue'
import KomodoIcon from 'shared/components/KomodoIcon.vue'

export const CHANGE_EVENT = 'change'

export enum Theme {
  Blue = 'blue',
  Black = 'black',
}

export default defineComponent({
  name: 'KomodoCheckbox',
  components: {
    KomodoInputTitle,
    KomodoIcon,
    KomodoErrors
  },
  model: {
    event: CHANGE_EVENT,
    prop: 'initialModel'
  },
  props: {
    title: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    value: {
      type: [Boolean, String, Array],
      default: undefined
    },
    trueValue: {
      type: [Boolean, String],
      default: true
    },
    falseValue: {
      type: [Boolean, String],
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    theme: {
      type: String as PropType<Theme>,
      default: Theme.Black
    },
    /**
   * The DOM id to set on the component
   */
    id: {
      type: String,
      default: undefined
    },
    initialModel: {
      type: [Boolean, Array, String],
      default: undefined
    }
  },
  emits: {
    change: (value: boolean) => value
  },
  setup (props, { emit }) {
    const inputRef = ref<HTMLInputElement | null>(null)
    const checked: Ref<boolean | undefined> = ref(false)
    const model = ref(props.initialModel)

    const errored = computed(() => {
      return props.errors.length > 0
    })

    watch(() => props.initialModel, (newInitialModel) => {
      model.value = newInitialModel
    })

    watch(() => model.value, () => {
      nextTick(() => {
        const input = inputRef.value
        if (input) checked.value = input.checked
      })
    })

    onMounted(() => {
      checked.value = inputRef.value?.checked
    })

    const onInputChange = (event) => {
      checked.value = event.target.checked
      emit(CHANGE_EVENT, model.value)
    }

    return {
      checked,
      errored,
      inputRef,
      model,
      onInputChange
    }
  }
})
</script>

<style lang="scss" scoped>
@mixin input-box-shadow($color) {
  box-shadow: 0 0 0 3px $color;
}

.komodo-input-checkmark {
  display: block;
  color: transparent;
  font-size: 16px;
}

.komodo-input-checkmark.komodo-blue {
  font-size: 15px;
  font-weight: 600;
}

.komodo-input {
  @include input-box-shadow(transparent);

  background-color: $gray-white;
  border-radius: 4px;
  border: 1px solid $gray-3-60;
  height: 18px;
  margin-right: 8px;
  transition: box-shadow 150ms;
  width: 18px;
}

.komodo-input.komodo-blue {
  border: 2px solid $action-blue-base;
}

.komodo-label {
  @include font-paragraph-p1-baseline;

  align-items: center;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
}

.komodo-label.komodo-blue {
  @include color-action-blue-base;

  font-weight: 600;
}

.komodo-checkbox {
  box-sizing: border-box;
  display: inline-block;

  *, *::before, *::after {
    box-sizing: inherit;
  }

  input[type='checkbox'] {
    position: absolute;
    visibility: hidden;
  }

  &.errored {
    .komodo-input {
      border-color: $accent-red-1-base;
    }
  }

  &.checked {
    .komodo-input-checkmark {
      color: $gray-white;
    }

    .komodo-input {
      @include background-action-blue-base;

      border-color: transparent;
    }
  }

  &.disabled {
    .komodo-input {
      @include background-gray-5-08;

      border-color: $gray-4-24;
    }

    .komodo-label {
      @include color-gray-4-24;

      cursor: not-allowed;
    }
  }

  &.checked.disabled {
    .komodo-input-checkmark {
      color: $gray-white;
    }

    .komodo-input {
      @include background-gray-4-24;

      border-color: transparent;
    }
  }

  &:not(.checked, .disabled) {
    .komodo-label:hover {
      .komodo-input {
        @include input-box-shadow($gray-5-08);
      }
    }
  }
}
</style>
